/**
 * Tenant Form Component
 *
 * Provides a form for creating and editing tenants with:
 * - Name and slug inputs
 * - Active status toggle
 * - Slug auto-generation from name
 * - Validation and error handling
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiClient, isSuccess } from '../../api/client';

// =============================================================================
// Types
// =============================================================================

/**
 * Tenant data structure
 */
export interface TenantData {
  id?: string;
  name: string;
  slug: string;
  active: boolean;
  settings?: Record<string, unknown>;
}

/**
 * Form validation errors
 */
interface FormErrors {
  name?: string;
  slug?: string;
  general?: string;
}

/**
 * TenantForm props
 */
interface TenantFormProps {
  /** Existing tenant data for editing (null for create mode) */
  tenant?: TenantData | null;
  /** Called when form is submitted successfully */
  onSubmit: (tenant: TenantData) => void;
  /** Called when form is cancelled */
  onCancel: () => void;
  /** Whether the form is in a loading state */
  isLoading?: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Generate slug from name
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 100);
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Form input component
 */
interface FormInputProps {
  label: string;
  id: string;
  type?: 'text' | 'textarea';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helpText?: string;
  error?: string;
  rows?: number;
}

function FormInput({
  label,
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  disabled,
  helpText,
  error,
  rows = 3,
}: FormInputProps) {
  const inputClasses = `w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
    error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={inputClasses}
        />
      ) : (
        <input
          type={type}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={inputClasses}
        />
      )}
      {helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Form toggle component
 */
interface FormToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function FormToggle({ label, description, checked, onChange, disabled }: FormToggleProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm font-medium text-gray-300">{label}</span>
        {description && (
          <p className="text-xs text-gray-500">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-discord-blurple' : 'bg-discord-light'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Tenant form component for creating and editing tenants
 */
export function TenantForm({
  tenant,
  onSubmit,
  onCancel,
  isLoading = false,
}: TenantFormProps) {
  const isEditMode = !!tenant?.id;

  // Form state
  const [name, setName] = useState(tenant?.name || '');
  const [slug, setSlug] = useState(tenant?.slug || '');
  const [active, setActive] = useState(tenant?.active ?? true);
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);

  /**
   * Auto-generate slug from name when not manually edited
   */
  useEffect(() => {
    if (!isSlugManuallyEdited && name) {
      setSlug(generateSlug(name));
    }
  }, [name, isSlugManuallyEdited]);

  /**
   * Handle name change
   */
  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setErrors((prev) => ({ ...prev, name: undefined }));
  }, []);

  /**
   * Handle slug change
   */
  const handleSlugChange = useCallback((value: string) => {
    setSlug(generateSlug(value));
    setIsSlugManuallyEdited(true);
    setErrors((prev) => ({ ...prev, slug: undefined }));
    setSlugAvailable(null);
  }, []);

  /**
   * Check slug availability
   */
  const checkSlugAvailability = useCallback(async () => {
    if (!slug || (isEditMode && slug === tenant?.slug)) {
      setSlugAvailable(null);
      return;
    }

    setIsCheckingSlug(true);
    const apiClient = getApiClient();

    try {
      const queryParams = isEditMode ? `?excludeId=${tenant?.id}` : '';
      const result = await apiClient.get<{ slug: string; available: boolean }>(
        `/tenants/check-slug/${slug}${queryParams}`
      );

      if (isSuccess(result)) {
        setSlugAvailable(result.data.available);
        if (!result.data.available) {
          setErrors((prev) => ({ ...prev, slug: 'This slug is already taken' }));
        }
      }
    } catch {
      // Ignore errors
    } finally {
      setIsCheckingSlug(false);
    }
  }, [slug, isEditMode, tenant?.id, tenant?.slug]);

  /**
   * Debounced slug availability check
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (slug && slug.length > 0) {
        void checkSlugAvailability();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [slug, checkSlugAvailability]);

  /**
   * Validate form
   */
  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be at most 100 characters';
    }

    if (!slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      newErrors.slug = 'Slug must be lowercase alphanumeric with hyphens only';
    } else if (slug.length > 100) {
      newErrors.slug = 'Slug must be at most 100 characters';
    } else if (slugAvailable === false) {
      newErrors.slug = 'This slug is already taken';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, slug, slugAvailable]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validate()) return;

      setIsSubmitting(true);
      setErrors({});

      const apiClient = getApiClient();

      try {
        const payload = {
          name: name.trim(),
          slug: slug.trim(),
          active,
        };

        let result;
        if (isEditMode) {
          result = await apiClient.put<TenantData>(`/tenants/${tenant?.id}`, payload);
        } else {
          result = await apiClient.post<TenantData>('/tenants', payload);
        }

        if (isSuccess(result)) {
          onSubmit(result.data);
        } else {
          setErrors({ general: result.error.message || 'Failed to save tenant' });
        }
      } catch (err) {
        setErrors({ general: 'Failed to save tenant. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [validate, name, slug, active, isEditMode, tenant?.id, onSubmit]
  );

  /**
   * Slug status indicator
   */
  const slugStatus = useMemo(() => {
    if (isCheckingSlug) {
      return (
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Checking...
        </span>
      );
    }

    if (slugAvailable === true) {
      return (
        <span className="text-xs text-green-400 flex items-center gap-1">
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Available
        </span>
      );
    }

    if (slugAvailable === false) {
      return (
        <span className="text-xs text-red-400 flex items-center gap-1">
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Taken
        </span>
      );
    }

    return null;
  }, [isCheckingSlug, slugAvailable]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Alert */}
      {errors.general && (
        <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
          <div className="flex items-start">
            <svg
              className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-red-200">{errors.general}</p>
            </div>
            <button
              type="button"
              onClick={() => setErrors((prev) => ({ ...prev, general: undefined }))}
              className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Form Fields */}
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6 space-y-4">
        <FormInput
          label="Tenant Name"
          id="name"
          value={name}
          onChange={handleNameChange}
          placeholder="My Organization"
          required
          disabled={isLoading || isSubmitting}
          error={errors.name}
          helpText="The display name for this tenant"
        />

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="slug" className="block text-sm font-medium text-gray-300">
              URL Slug
              <span className="text-red-400 ml-1">*</span>
            </label>
            {slugStatus}
          </div>
          <input
            type="text"
            id="slug"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="my-organization"
            disabled={isLoading || isSubmitting}
            className={`w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
              errors.slug
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
            } ${isLoading || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          {!errors.slug && (
            <p className="mt-1 text-xs text-gray-500">
              Used in URLs: /tenants/<span className="text-white">{slug || 'slug'}</span>
            </p>
          )}
          {errors.slug && <p className="mt-1 text-xs text-red-400">{errors.slug}</p>}
        </div>

        <FormToggle
          label="Active"
          description="Inactive tenants cannot access the system"
          checked={active}
          onChange={setActive}
          disabled={isLoading || isSubmitting}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || isSubmitting || isCheckingSlug}
          className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting
            ? isEditMode
              ? 'Saving...'
              : 'Creating...'
            : isEditMode
              ? 'Save Changes'
              : 'Create Tenant'}
        </button>
      </div>
    </form>
  );
}

export default TenantForm;
